// Tipos do cadastro de desconto v2. Spec: docs/superpowers/specs/2026-10-16-cadastro-desconto-v2-design.md
// O núcleo (model.ts) é TypeScript puro: nada de React nem de rede aqui.

export enum DiscountKind {
  default = 'default',
  special = 'special',
  reward = 'reward',
  recovery = 'recovery',
}

export enum StepKey {
  type = 'type',
  values = 'values',
  who = 'who',
  when = 'when',
  notice = 'notice',
  review = 'review',
}

export enum Audience {
  all = 'all',
  some = 'some',
  list = 'list',
  /** Ainda sem escolha: a cópia de uma promoção para uma lista de clientes não traz a lista, então o dono escolhe de novo. */
  unset = 'unset',
}

/** De onde veio a lista de clientes que trava o público. Sem origem conhecida o texto fica neutro. */
export enum ListOrigin {
  /** Segmentos de valor do Painel 360. */
  rfm = 'rfm',
  /** Botão "Criar Desconto" dos relatórios de clientes. */
  report = 'report',
}

export enum When {
  period = 'period',
  always = 'always',
}

export enum Hours {
  allday = 'allday',
  weekdays = 'weekdays',
  daily = 'daily',
}

export enum LimitMode {
  every = 'every',
  total = 'total',
}

export enum SendMode {
  now = 'now',
  later = 'later',
}

export enum RangeId {
  today = 'today',
  weekend = 'weekend',
  week = 'week',
  month = 'month',
}

export enum Frequency {
  never = 'never',
  daily = 'daily',
  weekly = 'weekly',
}

/** Grátis / Gasta pontos de uma opção de desconto. */
export enum PointsMode {
  free = 'free',
  pay = 'pay',
}

/** Tela do cadastro: preenchendo, salvando ou já salvo. */
export enum WizardView {
  form = 'form',
  saving = 'saving',
  saved = 'saved',
}

/** Estado de uma lista que vem da rede (Padrão em vigor, etiquetas, grupos, cidades). */
export enum DataState {
  loading = 'loading',
  error = 'error',
  ready = 'ready',
}

/** Uma opção de desconto de um combustível (até 3 por combustível). */
export interface Opt {
  /** Centavos de real por litro. null = ainda não preenchido. */
  cents: number | null
  payPoints: boolean
  /** Dígitos digitados (texto, para o input controlado). */
  pts: string
  /** O usuário confirmou um valor de R$ 1,00 ou mais. */
  okHigh: boolean
}

export interface FuelEntry {
  on: boolean
  opts: Opt[]
}

/** Janela de horário de um dia da semana (HH:mm). "O dia todo" é 00:00-23:59. */
export interface DayWindow {
  start: string
  end: string
}

/** Combustíveis marcados de largada: todos, nenhum ou uma lista de nomes normalizados. */
export type FuelPick = 'all' | 'none' | string[]

export interface Preset {
  pick: FuelPick
  cents: number | null
}

/** Tudo o que o usuário decide no cadastro. Dados puros, serializáveis. */
export interface Draft {
  kind: DiscountKind | null
  stations: string[]
  /** Só os combustíveis que o usuário já mexeu; o resto nasce do `preset`. */
  fuels: Record<string, FuelEntry>
  preset: Preset
  hasLimit: boolean
  limitN: string
  limitMode: LimitMode
  limitHours: string
  /** null = segue a configuração da rede. */
  earnPoints: boolean | null
  overlap: boolean
  audience: Audience
  tags: string[]
  groups: string[]
  cities: string[]
  useGroups: boolean
  useCity: boolean
  /** Vem do Painel 360 / relatórios. Trava o público em "lista de clientes". */
  customerIds: string[]
  listOrigin: ListOrigin | null
  when: When
  range: RangeId | null
  /** YYYY-MM-DD (horário de Brasília) */
  startDate: string
  endDate: string
  hours: Hours
  /** Domingo = 0 ... sábado = 6 */
  weekdays: boolean[]
  allDay: boolean
  /** HH:mm */
  startTime: string
  endTime: string
  /**
   * Horário de cada dia da semana (7 posições, null = dia desmarcado). Só nasce da cópia de um desconto semanal com
   * horários diferentes por dia: a tela mostra sem editar e o payload manda como está. null = um horário só.
   */
  dayWindows: (DayWindow | null)[] | null
  notify: boolean
  title: string
  pushText: string
  body: string
  sendMode: SendMode
  /** YYYY-MM-DDTHH:mm (horário de Brasília) */
  sendAt: string
  name: string
  nameEdited: boolean
}

export interface FieldError {
  /** id do elemento no DOM para onde o link do resumo leva o foco */
  id: string
  msg: string
}

// ─── Contexto: o que vem da rede (carregado fora do núcleo) ─────────────────────

export interface StationInfo {
  id: string
  name: string
  city?: string | null
}

export interface FuelInfo {
  id: string
  name: string
  /** Postos da rede que vendem este combustível. */
  stationIds: string[]
}

/** Uma opção do Padrão em vigor: centavos por litro e custo em pontos por abastecimento. */
export interface DefaultOpt {
  cents: number
  pts: number
}

/** Padrão em vigor (ativo) da rede. */
export interface DefaultInfo {
  id: string
  code: string
  stationIds: string[]
  /** fuelId -> maior desconto (centavos) */
  values: Record<string, number>
  /** fuelId -> todas as opções (centavos e pontos). Falta nos dados antigos: aí vale só o maior desconto, sem pontos. */
  opts?: Record<string, DefaultOpt[]>
}

export interface NamedItem {
  id: string
  name: string
}

/** Promoção recente da rede, para avisar de "parecido com". */
export interface RecentSpecial {
  code: string
  name: string
  createdAt: string
  stationIds: string[]
  fuelIds: string[]
  startDate?: string | null
  endDate?: string | null
}

export interface ChainInfo {
  name?: string
  appColor?: string | null
  useGift?: boolean
  useCustomerRecovery?: boolean
  useDotz?: boolean
  isPriceIncreaseEnabled?: boolean
  /** Configuração da rede "Pontuar quando usar desconto". */
  pointsWhenDiscounting?: boolean
}

export interface Counts {
  /** Clientes da rede com o app. */
  chain?: number | null
  /** Clientes que o público escolhido alcança (etiquetas ou grupos, e cidades). null = ainda não sabe. */
  audience?: number | null
  /** false = o número é um teto (a conta não pôde cruzar tudo). */
  audienceExact?: boolean
}

/** GET /chains/{id}/customer-city-stats: quantos clientes têm cidade no cadastro e quantos há em cada uma. */
export interface CityStats {
  total: number
  withCity: number
  /** cityId -> clientes */
  cities: Record<string, number>
}

export interface Ctx {
  now: Date
  chainId: string
  chain: ChainInfo
  stations: StationInfo[]
  fuels: FuelInfo[]
  defaults: DefaultInfo[]
  tags: NamedItem[]
  groups: NamedItem[]
  cities: NamedItem[]
  recent: RecentSpecial[]
  counts: Counts
  /** Falta = pronto. Enquanto o Padrão em vigor não chega, o cadastro não afirma que "não há Padrão". */
  defaultsState?: DataState
  cityStats?: CityStats | null
}

// ─── Payload do POST /discounts (allowlist do Joi, sem chave fora dela) ─────────

export interface FuelDiscountPayload {
  fuelId: string
  alterPriceValue: number
  alterPriceType: 'discount'
  alterPriceValueType: 'cents'
  costValueType: 'points'
  costValue: number
  uniqueDiscountValue: true
}

export interface WeekScheduleItem {
  dayOfTheWeek: number
  isActive: boolean
  startTime: string | null
  endTime: string | null
}

export interface NotificationPayload {
  title: string
  body: string
  pushNotification: true
  pushMessage: string
  sms: false
  sendNow: boolean
  sendAt: string | null
}

export interface DiscountPayload {
  chainId: string
  type: DiscountKind
  description?: string | null
  stationIds: string[]
  fuelDiscounts: FuelDiscountPayload[]
  usageLimit?: number | null
  intervalInHours?: number | null
  pointsWhenDiscounting?: boolean
  overlapsStandardDiscount?: boolean
  customerTagIds?: string[]
  customerGroupIds?: string[]
  cityIds?: string[]
  customerIds?: string[]
  isSchedule?: boolean
  frequency?: Frequency
  startAt?: string
  endAt?: string
  startTime?: string
  endTime?: string
  weekSchedual?: WeekScheduleItem[]
  hasNotification?: boolean
  notification?: NotificationPayload
}
